import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Trash2, Copy, Upload } from "lucide-react";

const Images = () => {
  const qc = useQueryClient();
  const [uploading, setUploading] = useState(false);

  const { data: files, isLoading } = useQuery({
    queryKey: ["admin_images"],
    queryFn: async () => {
      const { data, error } = await supabase.storage.from("site-images").list("blog", {
        limit: 100,
        sortBy: { column: "created_at", order: "desc" },
      });
      if (error) throw error;
      return data?.filter((f) => f.name !== ".emptyFolderPlaceholder") ?? [];
    },
  });

  const upload = async (file: File) => {
    setUploading(true);
    try {
      const ext = file.name.split(".").pop();
      const path = `blog/${Date.now()}.${ext}`;
      const { error } = await supabase.storage.from("site-images").upload(path, file);
      if (error) throw error;
      toast.success("Ladattu");
      qc.invalidateQueries({ queryKey: ["admin_images"] });
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setUploading(false);
    }
  };

  const remove = async (name: string) => {
    if (!confirm("Poistetaanko kuva?")) return;
    const { error } = await supabase.storage.from("site-images").remove([`blog/${name}`]);
    if (error) toast.error(error.message);
    else {
      toast.success("Poistettu");
      qc.invalidateQueries({ queryKey: ["admin_images"] });
    }
  };

  const urlOf = (name: string) =>
    supabase.storage.from("site-images").getPublicUrl(`blog/${name}`).data.publicUrl;

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <h2 className="font-display text-2xl font-semibold">Kuvat</h2>
        <label className="inline-flex cursor-pointer items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90">
          <Upload className="h-4 w-4" />
          {uploading ? "Ladataan..." : "Lataa kuva"}
          <input
            type="file"
            accept="image/*"
            disabled={uploading}
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) upload(f);
            }}
          />
        </label>
      </div>
      {isLoading ? (
        <p className="text-muted-foreground">Ladataan...</p>
      ) : files && files.length > 0 ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {files.map((f) => (
            <div key={f.name} className="overflow-hidden rounded-xl border bg-card">
              <img src={urlOf(f.name)} alt="" className="h-40 w-full object-cover" />
              <div className="flex items-center justify-between gap-2 p-3">
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(urlOf(f.name));
                    toast.success("URL kopioitu");
                  }}
                  className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
                >
                  <Copy className="h-3 w-3" /> Kopioi URL
                </button>
                <Button size="icon" variant="ghost" onClick={() => remove(f.name)}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="rounded-xl border bg-card p-6 text-center text-muted-foreground">
          Ei vielä kuvia. Lataa ensimmäinen yllä olevasta painikkeesta.
        </p>
      )}
    </div>
  );
};

export default Images;
